/**
 * TOML → JSON Tool
 *
 * Converts TOML into JSON.
 */

import { parse as parseToml } from 'smol-toml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

type IndentSize = '0' | '2' | '4' | 'tab';

export const TomlToJsonTool: ITransformerTool = {
  id: 'toml-to-json',
  name: 'TOML → JSON',
  description: 'Convert TOML to JSON',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['toml', 'json', 'convert', 'transform', 'config'],

  seo: {
    slug: 'toml-to-json-converter',
    pageTitle: 'TOML to JSON Converter - Free Online Tool | Prism',
    metaDescription: 'Convert TOML configuration files to JSON instantly. Supports nested tables, arrays, and all TOML data types. Fast, private, runs in your browser.',
    keywords: ['toml to json', 'toml converter', 'convert toml to json online', 'toml json tool', 'toml parser'],
    relatedTools: ['json-to-toml', 'toml-to-yaml', 'toml-validate'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.JSON,

  attributes: [
    {
      name: 'indentSize',
      type: 'select',
      label: 'Indent Size',
      defaultValue: '2',
      options: ['0', '2', '4', 'tab'],
      description: 'JSON indentation (0=minified)',
    },
    {
      name: 'sortKeys',
      type: 'boolean',
      label: 'Sort Keys',
      defaultValue: false,
      description: 'Sort object keys alphabetically',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    let parsed: unknown;

    try {
      parsed = parseToml(input);
    } catch (e) {
      const error = e as Error;
      throw ToolError.validation(`Invalid TOML: ${error.message}`, {
        suggestion: 'Check key-value syntax and ensure tables use [table] or [table.subtable] format',
      });
    }

    if (config.sortKeys) {
      parsed = sortObjectKeys(parsed);
    }

    const indentSize = (config.indentSize as IndentSize) || '2';
    const indent = indentSize === 'tab' ? '\t' : parseInt(indentSize, 10);

    return JSON.stringify(parsed, null, indent);
  },
};

function sortObjectKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }

  if (obj !== null && typeof obj === 'object') {
    const sorted: Record<string, unknown> = {};
    const keys = Object.keys(obj as Record<string, unknown>).sort();
    for (const key of keys) {
      sorted[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
    }
    return sorted;
  }

  return obj;
}
